import { useCallback, useEffect, useState } from 'react';
import type { CountryFieldState } from '../../shared/country_field/country_field.js';
import type { CountryCatalogItem } from '../../shared/country_field/country_options.js';
import type { CheckoutIo } from '../model/checkout_env.js';

interface CheckoutCountries {
  countries: CountryCatalogItem[];
  state: CountryFieldState;
}

const NO_COUNTRIES: CountryCatalogItem[] = [];

/** O catálogo de países do checkout pelo port (`getCountries`). FAIL-CLOSED:
 *  sem port, erro ou lista vazia ⇒ `unavailable`, e o campo mostra o valor
 *  gravado só-leitura — nunca um select sem opções (#4302). `reload` é a saída
 *  da falha passageira (o campo oferece "Tentar de novo"); trocar de idioma
 *  busca de novo, porque o servidor traduz o `label`. O cache é do host. */
export function useCheckoutCountries(
  io: CheckoutIo | null,
  locale: string,
): CheckoutCountries & { reload: () => void } {
  const getCountries = io?.getCountries;
  const [attempt, setAttempt] = useState(0);
  /** The last answer, tagged with the attempt it answers: a newer attempt is
   *  still loading (over the list in hand). */
  const [answer, setAnswer] = useState<(CheckoutCountries & { attempt: number }) | null>(null);

  useEffect(() => {
    if (!getCountries) return undefined;
    let cancelled = false;
    getCountries(locale)
      .then((countries) => {
        if (!cancelled) {
          setAnswer({ attempt, countries, state: countries.length > 0 ? 'ready' : 'unavailable' });
        }
      })
      .catch(() => {
        if (!cancelled) setAnswer({ attempt, countries: NO_COUNTRIES, state: 'unavailable' });
      });
    return () => {
      cancelled = true;
    };
  }, [getCountries, locale, attempt]);

  const reload = useCallback(() => {
    setAttempt((n) => n + 1);
  }, []);

  if (!getCountries) return { countries: NO_COUNTRIES, state: 'unavailable', reload };
  if (!answer) return { countries: NO_COUNTRIES, state: 'loading', reload };
  return {
    countries: answer.countries,
    state: answer.attempt === attempt ? answer.state : 'loading',
    reload,
  };
}
